import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Flex } from 'rebass';
import { useStore } from 'effector-react';
import { Trans, t } from '@lingui/macro';

import { CommonPageTemplate } from '@features/common';
import {
  Avatar,
  Button,
  Progress,
  SearchField,
  SelectBox,
  Typography,
} from '@oca/ui';
import { i18n } from '@lib/i18n';
import { getFirstLetters } from '@lib/help-fns';
import { Row, Col } from '@lib/grid';

import {
  $organization,
  $organizationFetching,
  $organizationGroups,
  $organizationSearch,
  $organizationDepartment,
  fetchOrganizationEffect,
  changeOrganizationSearch,
  changeOrganizationDepartment,
} from '../model/organization';

const positionLabels = {
  director: <Trans>Director</Trans>,
  manager: <Trans>Manager</Trans>,
  expert: <Trans>Expert</Trans>,
};

export function OrganizationChartPage() {
  const organization = useStore($organization);
  const groups = useStore($organizationGroups);
  const search = useStore($organizationSearch);
  const departmentId = useStore($organizationDepartment);
  const { isLoading, isFail } = useStore($organizationFetching);

  React.useEffect(() => {
    fetchOrganizationEffect();
  }, []);

  const departmentOptions = [
    { id: '', label: i18n._(t`All departments`) },
  ].concat(organization.departments);

  return (
    <CommonPageTemplate
      contentWidth={1}
      contentBackground="transparent"
      title={i18n._(t`Organization Chart`)}
      heading={i18n._(t`Organization Chart`)}
    >
      <Row alignItems="flex-end" spacing={2} pb={3}>
        <Col width={[1, 1 / 3, 1 / 4]}>
          <SelectBox
            label={i18n._(t`Department`)}
            options={departmentOptions}
            onChange={option => changeOrganizationDepartment(option.id)}
            value={
              departmentOptions.find(option => option.id === departmentId) ||
              null
            }
            menuPortalTarget={document.body}
          />
        </Col>
        <Col width="auto" flex="1 0 auto" mt={1}>
          <SearchField
            placeholder={i18n._(t`Search by name, job title or email`)}
            onChange={event => changeOrganizationSearch(event.target.value)}
            value={search}
            fullWidth
          />
        </Col>
      </Row>

      {isLoading && !organization.employees.length && (
        <StateBox>
          <Progress />
        </StateBox>
      )}

      {isFail && (
        <StateBox>
          <StateTitle>
            <Trans>The organization chart could not be loaded</Trans>
          </StateTitle>
          <Button variant="primary" onClick={() => fetchOrganizationEffect()}>
            <Trans>Try again</Trans>
          </Button>
        </StateBox>
      )}

      {!isLoading && !isFail && groups.length === 0 && (
        <StateBox>
          <StateTitle>
            {organization.employees.length ? (
              <Trans>No employees match your search</Trans>
            ) : (
              <Trans>No employees to show</Trans>
            )}
          </StateTitle>
        </StateBox>
      )}

      {groups.map(group => (
        <Box as="section" key={group.id === null ? 'none' : group.id} mb={4}>
          <Flex alignItems="baseline" mb={2}>
            <Typography variant="h3" as="h2">
              {group.name || <Trans>Other</Trans>}
            </Typography>
            <Count>{group.employees.length}</Count>
          </Flex>
          <Grid>
            {group.employees.map(employee => (
              <EmployeeCard key={employee.id} employee={employee} />
            ))}
          </Grid>
        </Box>
      ))}
    </CommonPageTemplate>
  );
}

function EmployeeCard({ employee }) {
  const name = employee.fullName || '';
  const title =
    employee.jobTitle || positionLabels[employee.position] || employee.position;
  const phones = [
    { kind: <Trans>Work</Trans>, number: employee.workPhone },
    { kind: <Trans>Mobile</Trans>, number: employee.mobilePhone },
  ].filter(phone => phone.number);

  return (
    <Card data-head={employee.isHead}>
      <Flex alignItems="center" mb={2}>
        <Avatar
          src={employee.avatarUrl}
          size={48}
          borderRadius={10}
          bg="#F5F3FF"
          color="#7B44F4"
          flexShrink={0}
        >
          {getFirstLetters(name)
            .slice(0, 2)
            .join('')}
        </Avatar>
        <Box ml={12} minWidth={0}>
          <Name title={name}>{name || '-'}</Name>
          {title && <JobTitle title={title}>{title}</JobTitle>}
        </Box>
      </Flex>
      {employee.isHead && (
        <HeadBadge>
          <Trans>Department head</Trans>
        </HeadBadge>
      )}
      <Contacts>
        {employee.email && (
          <ContactLine>
            <a href={`mailto:${employee.email}`}>{employee.email}</a>
          </ContactLine>
        )}
        {phones.map(phone => (
          <ContactLine key={phone.number}>
            <ContactKind>{phone.kind}</ContactKind>
            <a href={`tel:${phone.number}`} dir="ltr">
              {phone.number}
            </a>
          </ContactLine>
        ))}
      </Contacts>
    </Card>
  );
}

EmployeeCard.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fullName: PropTypes.string,
    jobTitle: PropTypes.string,
    position: PropTypes.string,
    email: PropTypes.string,
    workPhone: PropTypes.string,
    mobilePhone: PropTypes.string,
    avatarUrl: PropTypes.string,
    isHead: PropTypes.bool,
  }).isRequired,
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  grid-gap: 16px;
`;

const Card = styled.article`
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  min-width: 0;

  &[data-head='true'] {
    border-color: #d8b4fe;
  }
`;

const Name = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #181d27;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const JobTitle = styled.div`
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeadBadge = styled.span`
  display: inline-block;
  margin-bottom: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(123, 68, 244, 0.1);
  color: #7b44f4;
  font-size: 12px;
  font-weight: 600;
`;

const Contacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;

  a {
    color: #181d27;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a:hover,
  a:focus-visible {
    color: #7b44f4;
    text-decoration: underline;
  }
`;

const ContactLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const ContactKind = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: #64748b;
`;

const Count = styled.span`
  margin-left: 8px;
  font-size: 13px;
  color: #64748b;
`;

const StateBox = styled.div`
  padding: 48px 16px;
  text-align: center;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  background: #fafbfc;
`;

const StateTitle = styled.div`
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #475569;
`;
